import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Seo, type IndexablePath } from "../components/Seo";
import { useLp } from "../lib/i18n";
import type { CompareCell, ComparePageCopy } from "../lib/translations/pages/compare";

interface ComparePageProps {
  copy: ComparePageCopy;
  path: IndexablePath;
}

/**
 * Shared template for `/compare/<vendor>-vs-minuta` pages. Wrappers pass a
 * typed `ComparePageCopy` and the canonical route path. The template
 * renders hero, "pick the right one" honesty block, cited table,
 * "where Minuta wins" bullets, optional "Compare also" cross-link block,
 * and CTA. It also emits a `Product` JSON-LD whose `description` matches
 * the `seo.description` so meta and structured data stay in parity.
 */
export function ComparePage({ copy, path }: ComparePageProps) {
  const lp = useLp();
  const t = copy;

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title={t.seo.title} description={t.seo.description} path={path} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(productSchema(t))}
        </script>
      </Helmet>
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-margin dark:border-paper/10">
          <div className="mx-auto max-w-3xl px-6 pt-20 pb-16 sm:pt-28 sm:pb-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
              {t.eyebrow}
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink dark:text-paper">
              {t.h1}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink/75 dark:text-paper/75">
              {t.intro}
            </p>
          </div>
        </section>

        {/* Honesty: pick the right one */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.honesty.heading}
              </h2>

              <div className="mt-10 grid gap-6 md:grid-cols-2">
                <div className="rounded-2xl border border-ink/10 dark:border-paper/12 p-7 bg-ink/[0.02] dark:bg-paper/[0.02]">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
                    {t.honesty.pickThem.name}
                  </p>
                  <ul className="mt-4 space-y-3">
                    {t.honesty.pickThem.bullets.map((b) => (
                      <li
                        key={b}
                        className="text-[15px] leading-relaxed text-ink/75 dark:text-paper/75"
                      >
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-2xl border border-ink/10 dark:border-paper/12 p-7 bg-ink/[0.04] dark:bg-paper/[0.04]">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink dark:text-paper">
                    Minuta
                  </p>
                  <ul className="mt-4 space-y-3">
                    {t.honesty.pickMinuta.map((b) => (
                      <li
                        key={b}
                        className="text-[15px] leading-relaxed text-ink/85 dark:text-paper/85"
                      >
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Side-by-side cited table */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.table.heading}
              </h2>

              <div className="mt-10 overflow-x-auto rounded-2xl border border-ink/10 dark:border-paper/12">
                <table className="w-full text-left text-[14px]">
                  <thead className="bg-ink/[0.04] dark:bg-paper/[0.04]">
                    <tr>
                      <th className="px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink/65 dark:text-paper/65">
                        {t.table.headers.feature}
                      </th>
                      <th className="px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink/65 dark:text-paper/65">
                        {t.table.headers.them}
                      </th>
                      <th className="px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-ink dark:text-paper">
                        {t.table.headers.minuta}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink/8 dark:divide-paper/10">
                    {t.table.rows.map((r) => (
                      <tr key={r.feature}>
                        <td className="px-5 py-3.5 font-medium text-ink/85 dark:text-paper/85">
                          {r.feature}
                        </td>
                        <td className="px-5 py-3.5 text-ink/70 dark:text-paper/70">
                          <CitedCellView cell={r.them} />
                        </td>
                        <td className="px-5 py-3.5 text-ink dark:text-paper">
                          <CitedCellView cell={r.minuta} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="mt-4 font-mono text-[11px] text-ink/50 dark:text-paper/50">
                {t.table.footnote}
              </p>
            </div>
          </section>
        </Reveal>

        {/* Where Minuta wins */}
        <Reveal>
          <section className="border-b border-margin dark:border-paper/10">
            <div className="mx-auto max-w-3xl px-6 py-20">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.whenMinuta.heading}
              </h2>
              <ul className="mt-8 space-y-4">
                {t.whenMinuta.bullets.map((b) => (
                  <li
                    key={b}
                    className="text-[15px] leading-relaxed text-ink/80 dark:text-paper/80"
                  >
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        {/* Compare also — cross-links to sibling /compare pages */}
        {t.also.links.length > 0 && (
          <Reveal>
            <section className="border-b border-margin dark:border-paper/10">
              <div className="mx-auto max-w-3xl px-6 py-16">
                <p className="text-[15px] leading-relaxed text-ink/75 dark:text-paper/75">
                  {t.also.heading}{" "}
                  {t.also.links.map((link, i) => (
                    <span key={link.href}>
                      <Link
                        to={lp + link.href}
                        className="text-ink underline decoration-ink/30 underline-offset-4 hover:decoration-ink dark:text-paper dark:decoration-paper/30 dark:hover:decoration-paper"
                      >
                        {link.label}
                      </Link>
                      {i < t.also.links.length - 1 ? " · " : ""}
                    </span>
                  ))}
                </p>
              </div>
            </section>
          </Reveal>
        )}

        {/* CTA */}
        <Reveal>
          <section>
            <div className="mx-auto max-w-3xl px-6 py-24 text-center">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
                {t.cta.heading}
              </h2>
              <p className="mt-5 text-lg text-ink/70 dark:text-paper/70">
                {t.cta.body}
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to={lp + "/?auth=signup&plan=free"}
                  className="inline-flex items-center rounded-full bg-ink px-6 py-3 text-sm font-medium text-paper hover:bg-ink/90 transition-colors dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
                >
                  {t.cta.primary}
                </Link>
                <Link
                  to={lp + "/downloads"}
                  className="inline-flex items-center rounded-full border border-ink/15 dark:border-paper/15 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
                >
                  {t.cta.secondary}
                </Link>
              </div>
            </div>
          </section>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}

/**
 * Renders a {@link CompareCell}. When `cite` is present, appends a small
 * `↗` superscript anchor that opens the source URL in a new tab and
 * exposes the capture date via `title="captured {capturedAt}"`.
 */
function CitedCellView({ cell }: { cell: CompareCell }) {
  if (!cell.cite) return <>{cell.value}</>;
  return (
    <>
      {cell.value}
      <sup className="ml-1">
        <a
          href={cell.cite.url}
          target="_blank"
          rel="noopener noreferrer"
          title={`captured ${cell.cite.capturedAt}`}
          className="text-ink/55 hover:text-ink dark:text-paper/55 dark:hover:text-paper"
        >
          ↗
        </a>
      </sup>
    </>
  );
}

/**
 * Builds the per-page `Product` JSON-LD. `description` is taken verbatim
 * from the page meta description so the structured data and the
 * `<meta name="description">` stay in parity. `isRelatedTo` names the
 * compared vendor.
 */
function productSchema(copy: ComparePageCopy) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Minuta",
    description: copy.seo.description,
    brand: { "@type": "Brand", "name": "Minuta" },
    operatingSystem: "macOS 13+; Windows 10 21H2+",
    applicationCategory: "BusinessApplication",
    offers: { "@type": "Offer", price: "9", priceCurrency: "EUR" },
    isRelatedTo: {
      "@type": "Product",
      name: copy.vendor.name,
      url: copy.vendor.url,
    },
  };
}
